import { PaginatedResult } from 'src/app/shared/model/paginated-result';

export interface AuditLogChange {
  field: string;
  /** Valor anterior. null em `created` e em campos mascarados. */
  old: any;
  /** Valor novo. null em `deleted` e em campos mascarados. */
  new: any;
  /** O backend registrou que o campo mudou, mas nunca grava o valor. */
  masked?: boolean;
}

/**
 * Uma linha de `audit_logs` (App\Models\AuditLog). Ver AUDIT_LOGS_PROPOSTA.md,
 * seções 3 e 4.
 */
export interface AuditLog {
  id: string;
  /** Nome da TABELA auditada ('campaigns', 'lead_lists', ...). */
  auditable_type: string;
  auditable_id: string;
  /** Conta dona do registro (quem é afetado). */
  owner_user_id: string | null;
  /** created | updated | deleted | restored | login_* | evento sintético. */
  action: string;
  /** Quem fez. null quando partiu do sistema (job/console/webhook). */
  user_id: string | null;
  user_name: string | null;
  user_email: string | null;
  source: AuditLogSource;
  changed_fields: AuditLogChange[];
  /** Rota/comando que originou a alteração (ex.: `PUT /campaigns/{id}`). */
  context: string | null;
  ip: string | null;
  created_at: string;
}

export type AuditLogSource = 'dashboard' | 'app' | 'partner_api' | 'webhook' | 'console' | 'job';

/**
 * Página de GET /audit-logs. `applied_from` (Y-m-d) vem preenchido quando o
 * backend aplicou o período padrão (sem `from` e sem filtro de registro, conta
 * ou operador — `AUDIT_LIST_DEFAULT_DAYS`); null quando não aplicou.
 */
export type AuditLogPage = PaginatedResult<AuditLog[]> & { applied_from?: string | null };

/** Filtros de GET /audit-logs — todos combinados com AND. */
export interface AuditLogFilter {
  auditable_type?: string | null;
  auditable_id?: string | null;
  owner_user_id?: string | null;
  user_id?: string | null;
  action?: string | null;
  source?: string | null;
  /** Y-m-d, inclusivo. */
  from?: string | null;
  /** Y-m-d, inclusivo; não pode ser anterior a `from`. */
  to?: string | null;
}

export const AUDIT_LOG_SOURCES: { value: AuditLogSource; label: string }[] = [
  { value: 'dashboard', label: 'Painel' },
  { value: 'app', label: 'App' },
  { value: 'partner_api', label: 'API de parceiros' },
  { value: 'webhook', label: 'Webhook' },
  { value: 'console', label: 'Console' },
  { value: 'job', label: 'Job' },
];

/**
 * Trilha de login (LoginAuditService no backend): uma linha por tentativa de
 * entrada, com `auditable_type = 'user_logins'` e o resultado em `action`.
 * O detalhe (reason, method, email, user_agent, device_id, token_hash) vem em
 * `changed_fields`, com `old` sempre nulo.
 */
export const AUDIT_LOG_LOGIN_TYPE = 'user_logins';

export interface AuditLogActionOption {
  value: string;
  label: string;
  group: 'crud' | 'login';
}

export const AUDIT_LOG_ACTIONS: AuditLogActionOption[] = [
  { value: 'created', label: 'Criação', group: 'crud' },
  { value: 'updated', label: 'Edição', group: 'crud' },
  { value: 'deleted', label: 'Exclusão', group: 'crud' },
  { value: 'restored', label: 'Restauração', group: 'crud' },
  { value: 'login_success', label: 'Login com sucesso', group: 'login' },
  { value: 'login_failed', label: 'Login recusado', group: 'login' },
  { value: 'login_blocked', label: 'Login bloqueado', group: 'login' },
  { value: 'login_2fa_challenge', label: '2FA solicitado', group: 'login' },
  { value: 'login_2fa_failed', label: '2FA inválido', group: 'login' },
  { value: 'login_2fa_missing', label: '2FA não configurado', group: 'login' },
];

/** `reason` gravado em changed_fields nas linhas de login. */
export const AUDIT_LOG_LOGIN_REASONS: { [reason: string]: string } = {
  invalid_password: 'Senha errada',
  unknown_email: 'E-mail sem conta',
  too_many_attempts: 'Castigo por tentativas',
  account_inactive: 'Conta desativada',
  missing_device_id: 'App sem identificação do aparelho',
  two_factor_required: 'Perfil exige 2FA e a conta não tem',
  two_factor_too_many_attempts: 'Limite de códigos 2FA da conta',
  invalid_code: 'Código 2FA inválido',
  reused_code: 'Código 2FA já usado',
  invalid_token: 'Token desconhecido',
  token_expired: 'Token expirado',
  api_disabled: 'API desabilitada na conta',
  profile_not_allowed: 'Perfil sem acesso à API',
};

/** `method` gravado nas linhas de login. */
export const AUDIT_LOG_LOGIN_METHODS: { [method: string]: string } = {
  password: 'Senha',
  totp: 'Autenticador',
  recovery_code: 'Código de recuperação',
  token: 'Token da API',
};

/**
 * Tabelas auditadas (models com #[ObservedBy(AuditObserver::class)], seção 6
 * da proposta). Serve só de sugestão no filtro: o campo aceita qualquer nome de
 * tabela, então um cadastro novo no backend aparece sem depender desta lista.
 *
 * `users` vem de `user_edit_logs` (a mesma trilha do histórico de edições da
 * tela de usuários), que o backend junta na consulta. `user_logins` é a trilha
 * de login (AUDIT_LOG_LOGIN_TYPE).
 */
export const AUDIT_LOG_TABLES: { value: string; label: string }[] = [
  { value: 'users', label: 'Usuários' },
  { value: 'user_logins', label: 'Logins' },
  { value: 'campaigns', label: 'Campanhas' },
  { value: 'sequences', label: 'Sequências' },
  { value: 'lead_lists', label: 'Listas' },
  { value: 'broadcasts', label: 'Disparos' },
  { value: 'broadcast_calls', label: 'Disparos de ligação' },
  { value: 'integrations', label: 'Integrações' },
  { value: 'call4u_audios', label: 'Áudios Call4u' },
  { value: 'ai_agents', label: 'Agentes de IA' },
  { value: 'ai_agent_events', label: 'Eventos de agente de IA' },
  { value: 'ai_offers', label: 'Ofertas de IA' },
  { value: 'ai_list_settings', label: 'Configurações de IA da lista' },
  { value: 'meta_whatsapp_accounts', label: 'Contas WhatsApp (Meta)' },
  { value: 'partners_api_authentication', label: 'Tokens da API de parceiros' },
  { value: 'fractionation_groups', label: 'Fracionamentos' },
  { value: 'providers', label: 'Provedores' },
  { value: 'configurations', label: 'Configurações' },
  { value: 'user_recharges', label: 'Recargas' },
  { value: 'user_payments', label: 'Pagamentos' },
  { value: 'ai_model_prices', label: 'IA - Modelos' },
  { value: 'ai_token_packages', label: 'IA - Pacotes' },
  { value: 'ai_system_prompts', label: 'IA - Regras internas' },
  { value: 'ai_event_prompts', label: 'IA - Prompts de evento' },
  { value: 'platform_events', label: 'Eventos de plataforma' },
  { value: 'user_ai_settings', label: 'Configurações de IA do cliente' },
];
